import React, { useState, useEffect } from "react";
import "./batchupload.scss";
import { LoaderSpinner } from '../../../components/common/Loader';
import { downloadComplaintBatchTemplate, uploadComplaintBatchTemplate, validateComplaintBatchTemplate } from '../../../service/httpsCalls';
import https from "../../../service/httpsService";

export default function ComplaintBatchUpload() {
    const [isAuthorizing, setIsAuthorizing] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [validationErrors, setValidationErrors] = useState<any[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isUploadEnabled, setIsUploadEnabled] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const parseBoolean = (value: unknown): boolean | null => {
            if (typeof value === "boolean") return value;
            if (typeof value === "string") {
                const normalized = value.trim().toLowerCase();
                if (normalized === "true") return true;
                if (normalized === "false") return false;
            }
            return null;
        };

        const getCookieBoolean = (name: string): boolean | null => {
            const cookie = document.cookie
                .split("; ")
                .find(item => item.startsWith(`${name}=`));

            if (!cookie) return null;

            const value = decodeURIComponent(cookie.substring(name.length + 1));
            return parseBoolean(value);
        };

        const checkPermission = async () => {
            try {
                const response = await https.get("api/v1/Account/hasComplaintBatchUploadAccess");
                const apiAccess = parseBoolean(response?.data?.hasComplaintBatchUploadAccess);
                const cookieAccess = getCookieBoolean("HasComplaintBatchUploadAccess");
                const hasAccess = apiAccess ?? cookieAccess ?? false;

                if (isMounted) {
                    setHasAccess(hasAccess);
                }
            } catch {
                if (isMounted) {
                    setHasAccess(false);
                }
            } finally {
                if (isMounted) {
                    setIsAuthorizing(false);
                }
            }
        };

        checkPermission();
        return () => {
            isMounted = false;
        };
    }, []);

    if (isAuthorizing) {
        return (
            <div className="page-wrapper">
                <div className="page-content" style={{ display: "flex", justifyContent: "center", paddingTop: 40 }}>
                    <LoaderSpinner size={24} />
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="page-wrapper">
                <div className="page-content access-denied-wrap">
                    <div className="card access-denied-card">
                        <h4 className="access-denied-title">403 Forbidden</h4>
                        <p className="access-denied-message">
                            You do not have permission to access Complaint Batch Upload. If your access was recently updated, please refresh the page once and try again.
                        </p>
                    </div>
                </div>
            </div>
        );
    }
   
    // Handle File Selection   
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = e.target.files?.[0] || null;
        setFile(uploadedFile);
        setIsUploadEnabled(false);

        // Clear messages when file changes
        setSuccessMessage("");
        setErrorMessage("");
    };

    const handleClearFile = (preserveMessages: boolean = false) => {
        setFile(null);
        setIsUploadEnabled(false);
        if (!preserveMessages) {
            setSuccessMessage("");
            setErrorMessage("");
            setValidationErrors([]);
        }
        clearFileInput();
    };

    const clearFileInput = () => {
        const input = document.getElementById("batch-file-input") as HTMLInputElement | null;
        if (input) {
            input.value = "";
        }
    };

    // Validate Button Click    
    const handleValidate = async () => {
        if (!file) {
            setErrorMessage("Please upload a file before validating.");
            setSuccessMessage("");
            setIsUploadEnabled(false);
            return;
        }

        try {
            setIsValidating(true);
            setSuccessMessage("");
            setErrorMessage("");
            setValidationErrors([]);

            const res = await validateComplaintBatchTemplate(file, true);
            const payload = res?.data;
            if (payload?.success && (!payload.errors || payload.errors.length === 0)) {
                setSuccessMessage("The file was validated successfully, and no errors or formatting issues were found, you can proceed to upload batch processing.");
                setErrorMessage("");
                setIsUploadEnabled(true);
            } else {
                const errs = payload?.errors || [];
                setValidationErrors(errs);
                const msg = errs.length === 0
                    ? "We couldn't validate the file. Please verify the data and ensure all required fields are correctly filled."
                    : `Found ${errs.length} issue(s). Please review below.`;
                setErrorMessage(msg);
                setSuccessMessage("");
                setIsUploadEnabled(false);
                setFile(null);
            }
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                (typeof data === "object" && data?.errorMessage) ||
                (typeof data === "string" ? data : null) ||
                err?.message ||
                "Unable to validate file. Please try again.";
            setErrorMessage(message);
            setValidationErrors([]);
            setIsUploadEnabled(false);
            setFile(null);
        } finally {
            setIsValidating(false);
            clearFileInput();
        }
    };

   
    // Upload Button Click    
    const handleUpload = async () => {
        if (!file) {
            setErrorMessage("Please upload file before processing");
            setSuccessMessage("");
            setIsUploadEnabled(false);
            return;
        }

        try {
            setIsUploading(true);
            setSuccessMessage("");
            setErrorMessage("");

            const res = await uploadComplaintBatchTemplate(file, true);
            const payload = res?.data;

            if (payload?.success) {
                setSuccessMessage("The file has been uploaded successfully for the complaint batch. An email notification will be sent once all complaints have been processed.");
                setErrorMessage("");
                setValidationErrors([]);
                handleClearFile(true);
                setIsUploadEnabled(false);
            } else {
                const msg = payload?.errorMessage || "Unable to upload file. Please try again.";
                setErrorMessage(msg);
                setSuccessMessage("");
                setValidationErrors([]);
                setIsUploadEnabled(false);
            }
        } catch (err: any) {
            const data = err?.response?.data;
            const message =
                (typeof data === "object" && data?.errorMessage) ||
                (typeof data === "string" ? data : null) ||
                err?.message || "Unable to upload file. Please try again.";
            setErrorMessage(message);
            setValidationErrors([]);
            setIsUploadEnabled(false);
        } finally {
            setIsUploading(false);
        }
    };
   
    // Download Template Button Click    
    const handleDownloadTemplate = async () => {
        try {
            const res = await downloadComplaintBatchTemplate();
            const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const disposition = res.headers['content-disposition'];
            const fallbackName = "GrievanceBatchUploadTemplate.xlsx";
            if (disposition) {
                const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
                link.download = match ? match[1] : fallbackName;
            } else {
                link.download = fallbackName;
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setSuccessMessage("Template downloaded.");
            setErrorMessage("");
        } catch (err) {
            setErrorMessage("Unable to download template. Please try again.");
            setSuccessMessage("");
        }
    };

    return (
        <div className="page-wrapper">
            <div className="page-content">
                <div className="batchupload-container">

                    <h5 className="title">Offline Complaint Batch Upload</h5>

                    <div className="card">

                        <label className="label">Browse File</label>

                        <div className="file-row">
                            <input id="batch-file-input" type="file" onChange={handleFileChange} className="file-input" />
                            {file && (
                                <button
                                    type="button"
                                    className="remove-file-btn"
                                    onClick={() => handleClearFile()}
                                    aria-label="Remove file"> X
                                </button>
                            )}
                            <span className="file-name">{file ? file.name : "No file selected"}</span>
                        </div>

                        <div className="button-row">
                            <button className="btn btn-primary validating-btn" onClick={handleValidate} disabled={isValidating}>
                                {isValidating ? "Validating..." : "Validate File Data"}
                            </button>
                            <button className="btn btn-primary upload-btn" onClick={handleUpload} disabled={!isUploadEnabled || isUploading}>
                                {isUploading ? "Uploading..." : "Upload File To Process"}
                            </button>
                        </div>

                        {successMessage && <div className="status-message success-message">{successMessage}</div>}
                        {errorMessage && <div className="status-message error-message">{errorMessage}</div>}
                        {validationErrors && validationErrors.length > 0 && (
                            <div className="error-list">
                                <ul>
                                    {validationErrors.map((err, idx) => (
                                        <li key={idx}>
                                            Row {err.rowNumber}, {err.columnName}: {err.message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="template-section">
                            <br />
                            <h2>Download Batch Template (.xlsx)</h2>
                            <p className="template-text">
                                Please validate your file before uploading to ensure template compliance.
                                <br />                               
                                Once validated, proceed with upload to stage records for processing.                                
                                <br />
                                Please download the standardized Excel template for offline grievance entry. Ensure that all required fields are completed following the Instructions sheet included in the template. After submitting the file through the Batch Upload feature, the system will process the records in the background. You will receive an email notification upon completion of the batch processing.
                            </p>

                            <button className="btn btn-primary" onClick={handleDownloadTemplate}>Download Excel Template</button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

